import { lazy, Suspense, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Route, Router as WouterRouter, Switch, useLocation } from 'wouter';

import { ErrorBoundary } from '@/components/error-boundary';
import GbdLanding from '@/components/gbd-landing';
import NotFound from '@/pages/not-found';

const AuthShell = lazy(() => import('@/auth-shell'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
const AUTH_SESSION_MARKER = 'gbd-authenticated';

function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  return <ErrorBoundary resetKey={location}>{children}</ErrorBoundary>;
}

function AuthLoadingFallback() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-white/15 border-t-primary" aria-label="Loading authentication" />
    </div>
  );
}

function HomePage() {
  const shouldCheckSession = window.localStorage.getItem(AUTH_SESSION_MARKER) === 'true';

  return (
    <>
      <GbdLanding />
      {shouldCheckSession && (
        <Suspense fallback={null}>
          <AuthShell homeProbe />
        </Suspense>
      )}
    </>
  );
}

function ProtectedRoutes() {
  return (
    <Suspense fallback={<AuthLoadingFallback />}>
      <AuthShell />
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RoutedErrorBoundary>
        <Switch>
          <Route path="/" component={HomePage} />
          <Route path="/sign-in/*?" component={ProtectedRoutes} />
          <Route path="/sign-up/*?" component={ProtectedRoutes} />
          <Route path="/admin" component={ProtectedRoutes} />
          <Route component={NotFound} />
        </Switch>
      </RoutedErrorBoundary>
    </QueryClientProvider>
  );
}

export default function Root() {
  return (
    <WouterRouter base={basePath}>
      <App />
    </WouterRouter>
  );
}